import merge from 'lodash/merge';
import { useInViewport } from 'ahooks';
import { SIDE_SEP_LENGTH } from './dataFlow';
import { If } from 'common/src/components/if';
import type { IChartProps } from 'bizcharts/lib/interface';
import type { ILegend } from 'bizcharts/lib/components/Legend';
import Loading from 'common/src/components/loading/loading';
import type { IAreaGemoProps } from 'bizcharts/lib/geometry/Area';
import type { ILineGemoProps } from 'bizcharts/lib/geometry/Line';
import type { IPointGemoProps } from 'bizcharts/lib/geometry/Point';
import { ChartColorType, ICapabilityProps } from './interface';
import { breakTextByLimit } from 'common/src/components/text/break';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBizcharts } from '@umu/umu-react-hooks';
import LoadingV2 from 'common/src/components/loadingV2/loading';

/**
 * @desc 雷达图间隔样式
 */

const axisDefaultGrid = {
    line: {
        type: 'line',
        style: {
            stroke: '#D6DDE3', // 网格线的颜色
            lineWidth: 1, // 网格线的宽度复制代码
        },
    },
    alternateColor: ['rgb(250,251,255,0.6)', 'rgb(255,255,255,0.6)'],
};

/**
 * @desc 雷达图scale
 */
const scaleDefault = {
    value: {
        min: 0,
        max: 100,
        tick: 5,
        ticks: [0, 20, 40, 60, 80, 100],
        formatter: (val: number) => {
            return `${val}`;
        },
    },
};

const tooltipDefault = {
    shared: true,
};

const selectLabelDefaultConfig = {
    color: '#1e6ee6',
    defaultColor: '#222',
    isMultiSelect: false,
};

interface IProps extends ICapabilityProps {
    height?: number;
    isSwitchOnLabelSelect?: boolean;
    color?: ChartColorType;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/79#grid
    axisGrid?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/77#scale
    scale?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/80#shared
    tooltip?: any;
    selectLabelConfig?: {
        color?: string; // 选中颜色
        defaultColor?: string; // 默认颜色
        isMultiSelect?: boolean; // 是否多选
        textBaseline?: 'top' | 'middle' | 'bottom'; // 文本基线
    };
    labelOffset?: number;
    lineProps?: ILineGemoProps; // 线属性
    pointProps?: IPointGemoProps; // 点属性
    areaProps?: IAreaGemoProps; // 区域属性
    legendProps?: ILegend; // 图例属性
    radarChartProps?: IChartProps;
    compareChartType?: string;
    customTooltip?: null | ((xVal: string, info: any) => React.ReactNode);
    onLabelSelect?: (ids: string[]) => void;
}

function RadarChart(props: IProps) {
    const {
        data = [],
        height = 260,
        labelOffset = 20,
        isSwitchOnLabelSelect = false,
        axisGrid = axisDefaultGrid,
        selectLabelConfig,
        defaultSelected = [],
        color,
        tooltip = tooltipDefault,
        scale = scaleDefault,
        preCls,
        chartProps,
        hideTooltip = false,
        splitCount = SIDE_SEP_LENGTH,
        onLabelSelect,
        formatterLabel,
        customTooltip,
        compareChartType = 'type',
        legendProps = {},
        areaProps = {},
        lineProps = {},
        pointProps = {},
        radarChartProps = {},
    } = props;

    const radarRef = useRef(null);
    const labelSelectRef = useRef<string[]>([]);
    const [labelSelect, setLabelSelect] = useState<string[]>(defaultSelected);
    const [isShow, setIsShow] = useState(false);
    const [inViewport] = useInViewport(radarRef);
    const chartIns = useRef<any>(null);
    const [legendMap, setLegendMap] = useState<Record<string, boolean>>({});

    const labelConfig = useMemo(() => merge(selectLabelDefaultConfig, selectLabelConfig), [selectLabelConfig]);
    const defaultLabelConfig = {
        fontSize: 14,
        lineHeight: 20,
        fill: labelConfig.defaultColor,
        textBaseline: labelConfig.textBaseline,
    };

    const onChangeSelectLabel = (info: string[]) => {
        setLabelSelect(info);
        onLabelSelect?.(info);
    };

    const onSelectChange = (id: string) => {
        if (!isSwitchOnLabelSelect || !id) {
            return;
        }
        let newLabelList = labelSelectRef.current;
        const isHas = newLabelList.includes(id);
        const isMulti = labelConfig.isMultiSelect;
        if (isHas) {
            newLabelList = isMulti ? newLabelList.filter((item) => item !== id) : [];
        } else {
            newLabelList = isMulti ? [...newLabelList, id] : [id];
        }
        labelSelectRef.current = newLabelList;
        onChangeSelectLabel(newLabelList);
    };

    const onAxisLabelClick = (ev: any) => {
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        onSelectChange(id);
    };

    const onAxisLabelMouseover = (ev: any) => {
        if (!isSwitchOnLabelSelect) {
            return;
        }
        // 修改颜色 - 选中
        ev.target.attr('fill', labelConfig.color);
    };

    const onAxisLabelMouseleave = (ev: any) => {
        if (!isSwitchOnLabelSelect) {
            return;
        }
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        if (labelSelect.includes(id)) {
            return;
        }
        // 修改颜色 - 默认
        ev.target.attr('fill', labelConfig.defaultColor);
    };

    const onLegendChange: any = (ev: { item: any }) => {
        if (!chartIns.current) {
            return;
        }

        const { item } = ev;
        const { value } = item;
        const checked = !item.unchecked;
        legendMap[value] = checked;

        // 通过filter控制元素显示隐藏
        chartIns.current.filter(compareChartType, (val: string) => {
            const isShow = legendMap[val] !== false;
            legendMap[val] = isShow;
            return isShow;
        });
        setLegendMap({ ...legendMap });
        // 重新渲染，触发filter
        chartIns.current.render(true);
    };

    useEffect(() => {
        if (inViewport && !isShow) {
            setIsShow(true);
        }
    }, [inViewport]);

    useEffect(() => {
        onChangeSelectLabel([]);
    }, [data]);

    const BizChartsModules = useBizcharts();

    if (!BizChartsModules) {
        return <LoadingV2 />;
    }

    const { Chart, Line, Axis, Area, Point, Tooltip, Coordinate, Legend } = BizChartsModules;

    return (
        <div className={preCls} ref={radarRef}>
            <If condition={!isShow}>
                <Loading />
            </If>
            <If condition={isShow}>
                <Chart
                    height={height}
                    data={data}
                    autoFit
                    scale={scale}
                    padding={[20, 40, 20, 40]}
                    onGetG2Instance={(chart: any) => {
                        chartIns.current = chart;
                    }}
                    onAxisLabelClick={onAxisLabelClick}
                    onAxisLabelMouseover={onAxisLabelMouseover}
                    onAxisLabelMouseleave={onAxisLabelMouseleave}
                    {...chartProps}
                    {...radarChartProps}
                >
                    <Legend {...legendProps} onChange={onLegendChange} />
                    <Coordinate type="polar" radius={0.8} />
                    <Tooltip {...tooltip} visible={!hideTooltip}>
                        {customTooltip}
                    </Tooltip>
                    <Point position="label*value" color={color} shape="circle" {...pointProps} />
                    <Line
                        position="label*value"
                        size={2}
                        color={color}
                        style={{
                            lineWidth: 1,
                        }}
                        {...lineProps}
                    />
                    <Area
                        position="label*value"
                        color={color}
                        style={{
                            lineWidth: 1,
                            strokeOpacity: 1,
                            fillOpacity: 0.1,
                            opacity: 1,
                        }}
                        {...areaProps}
                    />
                    <Axis name="value" grid={axisGrid} label={null} tickLine={false} line={false} />
                    <Axis
                        name="label"
                        line={false}
                        tickLine={false}
                        label={{
                            autoHide: false,
                            autoEllipsis: true,
                            offset: labelOffset,
                            style: isSwitchOnLabelSelect
                                ? (_val, index) => {
                                      const itemInfo = data[index];
                                      return {
                                          ...defaultLabelConfig,
                                          fill: labelSelect.includes(itemInfo?.label)
                                              ? labelConfig.color
                                              : labelConfig.defaultColor,
                                      };
                                  }
                                : defaultLabelConfig,
                            formatter(text) {
                                const label = formatterLabel ? formatterLabel(text) : text;
                                return breakTextByLimit(label, 2, splitCount);
                            },
                        }}
                    />
                </Chart>
            </If>
        </div>
    );
}
export { RadarChart };
